import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  RadioGroup,
  Radio,
  Flex,
  Grid,
  Image,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiUpload, FiX, FiSave, FiFileText, FiArrowLeft } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { updateContent, getContentForEdit, ContentUpdateData } from '../services/contentUpdateService';
import { useAuth } from '../contexts/AuthContext';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { deleteContent } from '../services/contentDeletionService';
import { AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure } from '@chakra-ui/react';
import { useRef } from 'react';

const EditBookPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const inputBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.700', 'whiteAlpha.800');

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updateData, setUpdateData] = useState<ContentUpdateData>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleInputChange = (key: keyof ContentUpdateData, value: any) => {
    setUpdateData(prev => ({ ...prev, [key]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    const current = updateData.genres || [];
    const next = current.includes(genre)
      ? current.filter(g => g !== genre)
      : current.length < 3
        ? [...current, genre]
        : current;
    setUpdateData(prev => ({ ...prev, genres: next }));
  };

  const handleFileUpload = (key: keyof ContentUpdateData, file: File) => {
    setUpdateData(prev => ({ ...prev, [key]: file }));
    if (key === 'coverFile') {
      const url = URL.createObjectURL(file);
      setPreviewUrls(prev => ({ ...prev, coverFile: url }));
    }
    if (key === 'bookFile') {
      setPreviewUrls(prev => ({ ...prev, bookFile: 'selected' }));
    }
  };

  const loadContent = async () => {
    if (!id || !user?.uid) return;
    setIsLoading(true);
    try {
      const contentData = await getContentForEdit(id, user.uid);
      
      setUpdateData({
        title: contentData.title || '',
        description: contentData.description || '',
        author: (contentData as any).author || '',
        genres: contentData.genres || [],
        rating: (contentData as any).rating || (contentData as any).contentRating || 'All Ages',
        language: contentData.language || 'English',
        tags: (contentData as any).tags || '',
        cast: (contentData as any).cast || '',
        director: (contentData as any).director || '',
        writer: (contentData as any).writer || ''
      });
      
      // Set preview URLs for existing images
      setPreviewUrls({
        coverFile: contentData.poster || contentData.thumbnail || '',
        bookFile: contentData.assets?.bookFile ? 'existing' : ''
      });
    } catch (error) {
      console.error('Error loading content:', error);
      toast({ title: 'Failed to load book', status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.uid]);

  const handleSave = async () => {
    if (!id || !user?.uid) return;
    
    // Validation
    if (!updateData.title?.trim()) {
      toast({ title: 'Title is required', status: 'warning' });
      return;
    }
    if (!updateData.description?.trim()) {
      toast({ title: 'Description is required', status: 'warning' });
      return;
    }
    if (!updateData.genres || updateData.genres.length === 0) {
      toast({ title: 'At least one genre is required', status: 'warning' });
      return;
    }
    
    setIsSaving(true);
    try {
      const result = await updateContent(id, user.uid, updateData);
      
      if (result.success) {
        toast({ title: 'Book Updated', status: 'success' });
        navigate(`/book/${id}`);
      } else {
        toast({ title: 'Update failed', description: result.message, status: 'error' });
      }
    } catch (error) {
      console.error('Error updating book:', error);
      toast({ title: 'Update failed', status: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !user?.uid) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteContent(id, user.uid);
      
      if (result.success) {
        toast({
          title: 'Book Deleted Successfully',
          description: `Deleted ${result.deletedItems.files} files and all associated data.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        navigate('/creator-dashboard');
      } else {
        toast({
          title: 'Delete Failed',
          description: result.message || 'Failed to delete book. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Error deleting book:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onDeleteClose();
    }
  };

  if (isLoading) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <HStack justify="space-between" mb={4}>
          <Button variant="ghost" onClick={() => navigate(-1)} leftIcon={<IconWrapper icon={FiArrowLeft} size={16} />}>Back</Button>
        </HStack>
        <VStack gap={4} align="center" py={20}>
          <Text>Loading book details...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
      <HStack justify="space-between" mb={4}>
        <Button variant="ghost" onClick={() => navigate(-1)} leftIcon={<IconWrapper icon={FiArrowLeft} size={16} />}>Back</Button>
        <HStack>
          <Button variant="outline" onClick={() => navigate(`/book/${id}`)}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} leftIcon={<IconWrapper icon={FiSave} size={16} />}>Save Changes</Button>
        </HStack>
      </HStack>

      <VStack gap={6} align="stretch">
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">Edit Book</Text>

        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
          <VStack gap={4} align="stretch">
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">Book Title *</FormLabel>
              <Input value={updateData.title || ''} onChange={(e) => handleInputChange('title', e.target.value)} placeholder="Enter book title" bg={inputBg} borderColor={borderColor} />
            </FormControl>
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">Author</FormLabel>
              <Input value={updateData.author || ''} onChange={(e) => handleInputChange('author', e.target.value)} placeholder="Enter author name" bg={inputBg} borderColor={borderColor} />
            </FormControl>
          </VStack>

          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Description *</FormLabel>
            <Textarea value={updateData.description || ''} onChange={(e) => handleInputChange('description', e.target.value)} placeholder="Enter book description" bg={inputBg} borderColor={borderColor} rows={6} />
          </FormControl>
        </Grid>

        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">Genres * (Select up to 3)</FormLabel>
          <Flex wrap="wrap" gap={2}>
            {GENRES.map(genre => (
              <Button key={genre} size="sm" variant={(updateData.genres || []).includes(genre) ? 'solid' : 'outline'} colorScheme="purple" onClick={() => handleGenreToggle(genre)} isDisabled={!(updateData.genres || []).includes(genre) && (updateData.genres || []).length >= 3}>
                {genre}
              </Button>
            ))}
          </Flex>
        </FormControl>

        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Content Rating</FormLabel>
            <RadioGroup value={updateData.rating || 'All Ages'} onChange={(value) => handleInputChange('rating', value)}>
              <HStack gap={2} wrap="wrap">
                {CONTENT_RATINGS.map(rating => (
                  <Radio key={rating.value} value={rating.value} colorScheme="purple" size="sm">{rating.label}</Radio>
                ))}
              </HStack>
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Language</FormLabel>
            <Select value={updateData.language || 'English'} onChange={(e) => handleInputChange('language', e.target.value)} bg={inputBg} borderColor={borderColor}>
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="Chinese">Chinese</option>
              <option value="Japanese">Japanese</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Tags</FormLabel>
            <Input value={updateData.tags || ''} onChange={(e) => handleInputChange('tags', e.target.value)} placeholder="Enter tags (comma separated)" bg={inputBg} borderColor={borderColor} />
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Book Cover</FormLabel>
            <Box border="2px dashed" borderColor={previewUrls.coverFile ? 'purple.300' : borderColor} borderRadius="lg" p={6} textAlign="center" bg={previewUrls.coverFile ? 'purple.50' : inputBg} _dark={{ bg: previewUrls.coverFile ? 'purple.900' : 'gray.700' }} cursor="pointer" _hover={{ borderColor: 'purple.400' }} transition="all 0.2s" onClick={() => document.getElementById('cover-upload')?.click()} minH="180px" display="flex" alignItems="center" justifyContent="center">
              {previewUrls.coverFile ? (
                <VStack gap={3}>
                  <Box position="relative">
                    <Image src={previewUrls.coverFile} alt="Cover preview" maxH="120px" maxW="160px" objectFit="cover" borderRadius="md" />
                    <Button position="absolute" top={-2} right={-2} size="sm" colorScheme="red" variant="solid" borderRadius="full" onClick={(e) => { e.stopPropagation(); setUpdateData(prev => ({ ...prev, coverFile: undefined })); setPreviewUrls(prev => { const n = { ...prev }; delete n.coverFile; return n; }); }}>
                      <IconWrapper icon={FiX} size={12} />
                    </Button>
                  </Box>
                  <Text fontSize="sm" color="gray.600">Click to change cover</Text>
                </VStack>
              ) : (
                <VStack gap={3}>
                  <IconWrapper icon={FiUpload} size={28} color="gray.400" />
                  <Text fontSize="sm" color="gray.600">Upload book cover</Text>
                  <Text fontSize="xs" color="gray.500">JPG, PNG, WEBP (Max 200MB)</Text>
                </VStack>
              )}
              <Input id="cover-upload" type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('coverFile', file); }} display="none" />
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">Book File * (PDF only)</FormLabel>
            <Box border="2px dashed" borderColor={previewUrls.bookFile ? 'purple.300' : borderColor} borderRadius="lg" p={6} bg={previewUrls.bookFile ? 'purple.50' : inputBg} _dark={{ bg: previewUrls.bookFile ? 'purple.900' : 'gray.700' }} minH="180px" display="flex" alignItems="center" justifyContent="center">
              <VStack gap={4} w="100%">
                <IconWrapper icon={FiFileText} size={32} color="gray.400" />
                <VStack gap={2}>
                  <Text color={labelColor} fontSize="sm" textAlign="center">{previewUrls.bookFile ? (updateData.bookFile as File)?.name || 'Book file selected' : 'No file selected'}</Text>
                  <HStack>
                    {previewUrls.bookFile ? (
                      <Button size="sm" colorScheme="red" variant="outline" onClick={() => { setUpdateData(prev => ({ ...prev, bookFile: undefined })); setPreviewUrls(prev => { const n = { ...prev }; delete n.bookFile; return n; }); }}>Remove</Button>
                    ) : (
                      <Button size="sm" colorScheme="purple" onClick={() => document.getElementById('book-file-edit-upload')?.click()}>Select File</Button>
                    )}
                  </HStack>
                </VStack>
              </VStack>
              <Input id="book-file-edit-upload" type="file" accept=".pdf,application/pdf" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload('bookFile', file); }} display="none" />
            </Box>
          </FormControl>
        </Grid>

        <HStack justify="flex-end" gap={3}>
          <Button variant="outline" onClick={() => navigate(`/book/${id}`)}>Cancel</Button>
          <Button colorScheme="red" variant="outline" onClick={onDeleteOpen} isDisabled={isDeleting}>
            Delete Book
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} leftIcon={<IconWrapper icon={FiSave} size={16} />}>Save Changes</Button>
        </HStack>
      </VStack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Book Permanently
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "{updateData.title || 'this book'}"? This action cannot be undone and will permanently remove:
              <br />
              <br />
              • All book files and media
              <br />
              • All comments and interactions
              <br />
              • All analytics and view data
              <br />
              • All associated metadata
              <br />
              <br />
              <Text fontWeight="bold" color="red.500">
                This action is irreversible!
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting}>
                Delete Forever
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default EditBookPage;


